'use client'

import { useMemo, useState } from 'react'
import { PlanCard } from '@/components/plan-card'
import type { BusinessPlanPricing, BusinessPricingTier, BusinessSingleWallPrice } from '@/lib/pricing'

const ONE_TIME_WALL = {
  title: 'Single Wall',
  price: 'Pay once',
  note: 'for one celebration',
  description: 'Perfect for one wall with essential features and one-time pricing.',
  cta: 'Create a Wall',
  features: ['One collaborative celebration wall', 'Messages, photos, GIFs & video', 'Shareable contribution link', 'Wall moderation controls'],
}

const ENTERPRISE = {
  title: 'Enterprise',
  price: 'Let’s talk',
  note: 'for larger organizations',
  description: 'For large organizations needing custom rollout and support.',
  cta: 'Contact Sales',
  features: ['Everything in Professional', 'Enterprise onboarding', 'SSO & advanced access controls', 'Custom governance options', 'Dedicated support'],
}

function formatMonthly(unitAmount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(unitAmount / 100)
}

function priceForCount(tiers: BusinessPricingTier[], employeeCount: number): BusinessPricingTier | undefined {
  return tiers.find((tier) => tier.employeeCount === employeeCount)
}

export function BusinessPlanCalculator({ pricing, singleWallPrice }: { pricing: BusinessPlanPricing; singleWallPrice: BusinessSingleWallPrice | null }) {
  const employeeCounts = useMemo(
    () => Array.from(new Set([...pricing.starter, ...pricing.professional].map((t) => t.employeeCount))).sort((a, b) => a - b),
    [pricing]
  )
  const [starterEmployeeCount, setStarterEmployeeCount] = useState(employeeCounts[0] ?? 50)
  const [professionalEmployeeCount, setProfessionalEmployeeCount] = useState(employeeCounts[0] ?? 50)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  const starterTier = priceForCount(pricing.starter, starterEmployeeCount)
  const professionalTier = priceForCount(pricing.professional, professionalEmployeeCount)

  const displayPrice = (tier: BusinessPricingTier | undefined): string => {
    if (!tier) return 'Team pricing'
    if (billingCycle === 'yearly') {
      const annualAmount = tier.yearlyUnitAmount ?? tier.monthlyUnitAmount * 12
      return formatMonthly(Math.round(annualAmount / 12), pricing.currency)
    }
    return formatMonthly(tier.monthlyUnitAmount, pricing.currency)
  }

  const billingNote = (tier: BusinessPricingTier | undefined, employeeCount: number): string => {
    if (billingCycle === 'yearly' && tier) {
      const annualAmount = tier.yearlyUnitAmount ?? tier.monthlyUnitAmount * 12
      return `${formatMonthly(annualAmount, pricing.currency)} billed annually · up to ${employeeCount} employees`
    }
    return `per month · up to ${employeeCount} employees`
  }

  const employeeCountControl = (value: number, onChange: (value: number) => void, id: string) => (
    <label htmlFor={id} className="mt-5 block text-xs font-semibold text-[#6B6170]">
      Company size
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(Number.parseInt(event.target.value, 10))}
        className="mt-2 w-full rounded-xl border border-[#D7D9D7] bg-white px-3 py-2 text-sm font-semibold text-[#2B0A4A] focus:border-[#2B0A4A] focus:outline-none focus:ring-2 focus:ring-[#2B0A4A]/15"
      >
        {employeeCounts.map((count) => (
          <option key={count} value={count}>
            Up to {count} employees
          </option>
        ))}
      </select>
    </label>
  )

  return (
    <>
      <div className="mb-10 mt-10 flex justify-center">
        <div className="inline-flex gap-1 rounded-full border border-[#D7D9D7] bg-[#F6F7F2] p-1" role="tablist" aria-label="Billing cycle">
          {(['monthly', 'yearly'] as const).map((cycle) => (
            <button
              key={cycle}
              type="button"
              role="tab"
              aria-selected={billingCycle === cycle}
              onClick={() => setBillingCycle(cycle)}
              className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition ${billingCycle === cycle ? 'border border-[#1E2F2D] bg-white text-[#2F3A36] shadow-sm' : 'text-[#6B6170] hover:text-[#2B0A4A]'}`}
            >
              {cycle}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <PlanCard
          {...ONE_TIME_WALL}
          price={singleWallPrice ? formatMonthly(singleWallPrice.unitAmount, singleWallPrice.currency) : ONE_TIME_WALL.price}
          note={singleWallPrice ? 'per wall · one-time purchase' : ONE_TIME_WALL.note}
          features={singleWallPrice?.features.length ? singleWallPrice.features : ONE_TIME_WALL.features}
          businessTone="single"
        />
        <PlanCard
          title="Starter"
          description="For growing organizations running recurring team walls."
          price={displayPrice(starterTier)}
          note={billingNote(starterTier, starterEmployeeCount)}
          cta="Choose Starter"
          features={pricing.starterFeatures}
          businessTone="starter"
          control={employeeCountControl(starterEmployeeCount, setStarterEmployeeCount, 'employee-count-starter')}
        />
        <PlanCard
          title="Professional"
          description="Advanced execution for high-volume contributor engagement."
          price={displayPrice(professionalTier)}
          note={billingNote(professionalTier, professionalEmployeeCount)}
          cta="Choose Professional"
          badge="Recommended"
          features={pricing.professionalFeatures}
          businessTone="professional"
          control={employeeCountControl(professionalEmployeeCount, setProfessionalEmployeeCount, 'employee-count-professional')}
          featured
        />
        <PlanCard {...ENTERPRISE} features={pricing.enterpriseFeatures} businessTone="enterprise" />
      </div>

      <div className="mt-8 text-sm text-[#6B6170]">
        <b>Fair employee-based pricing.</b> Starter and Professional pricing scale with the employee count you select above.
      </div>
    </>
  )
}
